// BootstrapMapper — pure section → store-shape mappers (contract 07 §5/§8). Ported as-is from
// v3: no store, HTTP, or Vue import; every function is pure and independently testable.
import type { AccountItem } from '../models/account.types'
import type {
  BootstrapResponse,
  MappedBootstrapResult,
  AccountCoreSection,
  AccountDetailSection,
  WorkspaceCoreSection,
  DimensionsSection,
  CompositionsSection,
  IntegrationsSection,
  SettingsSection,
  SessionAccessSection,
  RbacSection,
  FeatureFlagsSection,
  ReportsSection,
  ReportNavItem,
  SchedulesSection,
  IntegrationsConnectedSection,
  DashboardAccessSection,
  TableViewConfigItem,
  BootstrapScheduleGroup,
  BootstrapSection,
  BootstrapSectionStatus,
  CompositionRawItem,
  DegradedSection,
} from '../models/bootstrap.types'

// --- session_access ---

export function mapUser(section: SessionAccessSection | null | undefined) {
  return section?.user ?? null
}

export function mapAccounts(
  section: SessionAccessSection | null | undefined,
): AccountItem[] | null {
  if (!section?.accessible_accounts?.data) return null
  return section.accessible_accounts.data
    .filter((a: AccountItem) => a.name)
    .sort((a: AccountItem, b: AccountItem) => Number(b.id) - Number(a.id))
}

export function mapUserAccessInfo(section: SessionAccessSection | null | undefined) {
  return section?.user_access_info ?? null
}

// --- account_core ---

export function mapAccountCenters(section: AccountCoreSection | null | undefined) {
  return section?.account_center ?? null
}

export function mapWorkspaces(section: AccountCoreSection | null | undefined) {
  if (!section?.workspaces) return null
  return [...section.workspaces].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  )
}

export function mapSchedules(
  section: AccountCoreSection | null | undefined,
): Record<string, unknown[]> | null {
  if (!section?.schedules) return null
  const result: Record<string, unknown[]> = {}
  section.schedules.forEach((group: BootstrapScheduleGroup) => {
    result[group.workspace_id] = group.schedules.filter(
      (d) => d.additional_info || d.report_id === 12,
    )
  })
  return result
}

// --- account_detail ---

export function mapAccountDetail(section: AccountDetailSection | null | undefined) {
  return section ?? null
}

// --- workspace_core ---

export function mapPreferences(section: WorkspaceCoreSection | null | undefined) {
  return section?.preferences?.data ?? null
}

export function mapTableViewConfigs(
  section: WorkspaceCoreSection | null | undefined,
): Record<string, unknown> | null {
  if (!section?.table_view_configs || !Array.isArray(section.table_view_configs)) return null
  if (section.table_view_configs.length === 0) return {}
  const obj: Record<string, TableViewConfigItem> = {}
  for (const item of section.table_view_configs) {
    obj[item.table_id] = item
  }
  return obj
}

export function mapDashboards(section: WorkspaceCoreSection | null | undefined) {
  return section?.custom_dashboards?.data ?? null
}

export function mapCustomDashboardUser(
  section: WorkspaceCoreSection | null | undefined,
): boolean | null {
  if (!section?.custom_dashboards) return null
  return section.custom_dashboards.custom_dashboard_user ?? false
}

export function mapWsAttributesFromDashboards(
  section: WorkspaceCoreSection | null | undefined,
  workspaceId: number | string | null,
): Record<string, unknown> | null {
  if (!section?.custom_dashboards?.workspace_attributes || !workspaceId) return null
  return { [workspaceId]: section.custom_dashboards.workspace_attributes }
}

export function mapWsIntegrationsFromDashboards(
  section: WorkspaceCoreSection | null | undefined,
  workspaceId: number | string | null,
): Record<string, unknown> | null {
  if (!section?.custom_dashboards?.workspace_integrations || !workspaceId) return null
  return { [workspaceId]: section.custom_dashboards.workspace_integrations }
}

// --- dimensions ---

export function mapWorkspaceLevels(
  section: DimensionsSection | null | undefined,
): Record<string, unknown[]> | null {
  if (!section?.table_columns_overview_all?.dimension_groups) return null
  const groups = section.table_columns_overview_all.dimension_groups
  if (!groups.length) return null
  const result: Record<string, unknown[]> = {}
  for (const group of groups) {
    result[group.workspace_id] = group.dimension_groups || []
  }
  return result
}

export function mapDropdownables(
  section: DimensionsSection | null | undefined,
): Record<string, unknown> | null {
  return section?.dropdownable_dimensions?.data?.dropdownable_dimensions ?? null
}

// --- compositions ---

export function mapCompositions(section: CompositionsSection | null | undefined): unknown[] | null {
  if (!section?.data) return null
  return section.data.map((d: CompositionRawItem) => ({
    ...d,
  }))
}

// --- integrations ---

export function mapAllowedIntegrations(section: IntegrationsSection | null | undefined) {
  return section?.allowed_integrations?.data?.allowed_integrations ?? null
}

export function mapIntegrationGroups(section: IntegrationsSection | null | undefined) {
  return section?.allowed_integrations?.data?.integration_groups ?? null
}

export function mapAllIntegrations(section: IntegrationsSection | null | undefined) {
  return section?.all_integrations ?? null
}

export function mapConnectableIntegrations(section: IntegrationsSection | null | undefined) {
  return section?.connectable_integrations?.data ?? null
}

// --- settings ---

export function mapAttributes(section: SettingsSection | null | undefined) {
  return section?.key_events?.data ?? null
}

// --- rbac ---

/** Passed through whole — the store keeps the authoritative payload verbatim, and `permissions`
 *  stays the raw value-MAP (never flattened to a key list: `getPermission` returns numbers and
 *  scope arrays). A failed/absent section maps to `null`, which the store reads as "no grant". */
export function mapRbac(section: RbacSection | null | undefined): RbacSection | null {
  return section ?? null
}

// --- feature_flags ---

/** `enabled_features` verbatim. An EMPTY list maps to `[]`, never `null` — "you have none" is an
 *  explicit value the hydrator must write (section_status `empty` ≠ `failed`). */
export function mapFeatureFlags(section: FeatureFlagsSection | null | undefined): string[] | null {
  return section?.enabled_features ?? null
}

// --- reports / schedules / integrations_connected / dashboard_access (BE-ACC-03/-05) ---
// Mapped close to the wire; the owning modules' registered hydrators pick their slice. Same
// empty-vs-absent rule everywhere: `[]` is an explicit empty, `null` means failed/cold/absent.

export function mapReports(section: ReportsSection | null | undefined): ReportNavItem[] | null {
  return section?.data ?? null
}

/** The `schedules` SECTION (slim nav view; module 08 owns the consuming store, R3) — distinct
 *  from `mapSchedules` above, which maps the account_core embed. */
export function mapNavSchedules(
  section: SchedulesSection | null | undefined,
): BootstrapScheduleGroup[] | null {
  return section?.data ?? null
}

export function mapIntegrationsConnected(
  section: IntegrationsConnectedSection | null | undefined,
): string[] | null {
  return section?.data ?? null
}

export function mapDashboardAccess(
  section: DashboardAccessSection | null | undefined,
): DashboardAccessSection | null {
  return section ?? null
}

// --- section status / degraded classification (BE-ACC-06) ---

/** `meta.section_status` normalised: `null` when the payload predates BE-ACC-06 (additive
 *  tolerance) — consumers must then fall back to null-based payload semantics. */
export function mapSectionStatus(
  response: BootstrapResponse,
): Partial<Record<BootstrapSection, BootstrapSectionStatus>> | null {
  return response.meta?.section_status ?? null
}

/**
 * The degraded-ready record: every requested section this response could NOT resolve.
 *   • `errors[]` entries → `failed`, with `retryable` normalised (absent on older payloads →
 *     `true`, the backend's own "unknown → transient" rule).
 *   • `section_status === 'failed'` without an `errors[]` entry (defensive — the contract pairs
 *     them) → `failed`, retryable `true`.
 *   • `section_status === 'cold'` (requested, resolved from neither cache nor fresh build; no
 *     `errors[]` entry by design) → `cold`, retryable `true` — a `refresh` re-ask is the remedy.
 * `empty` NEVER lands here: "you have none" is a resolved outcome, not a degradation. With
 * `section_status` absent (pre-BE-ACC-06) the list is derived from `errors[]` alone — the
 * null-based fallback (no cold detection possible).
 */
export function mapDegradedSections(response: BootstrapResponse): DegradedSection[] {
  const degraded = new Map<string, DegradedSection>()
  for (const entry of response.errors ?? []) {
    degraded.set(entry.section, {
      section: entry.section,
      status: 'failed',
      retryable: entry.retryable ?? true,
    })
  }
  for (const [section, status] of Object.entries(response.meta?.section_status ?? {})) {
    if (degraded.has(section)) continue
    if (status === 'failed' || status === 'cold') {
      degraded.set(section, { section, status, retryable: true })
    }
  }
  return [...degraded.values()]
}

// --- Full response mapper ---

export function mapBootstrapResponse(response: BootstrapResponse): MappedBootstrapResult {
  const workspaceId = response.meta?.workspace_id

  return {
    // session_access
    user: mapUser(response.session_access),
    accounts: mapAccounts(response.session_access),
    userAccessInfo: mapUserAccessInfo(response.session_access),
    // account_core
    accountCenters: mapAccountCenters(response.account_core),
    workspaces: mapWorkspaces(response.account_core),
    schedules: mapSchedules(response.account_core),
    // account_detail
    accountDetail: mapAccountDetail(response.account_detail),
    // workspace_core
    preferences: mapPreferences(response.workspace_core),
    tableViewConfigs: mapTableViewConfigs(response.workspace_core),
    dashboards: mapDashboards(response.workspace_core),
    customDashboardUser: mapCustomDashboardUser(response.workspace_core),
    wsAttributes: mapWsAttributesFromDashboards(response.workspace_core, workspaceId),
    wsIntegrations: mapWsIntegrationsFromDashboards(response.workspace_core, workspaceId),
    // dimensions
    workspaceLevels: mapWorkspaceLevels(response.dimensions),
    dropdownables: mapDropdownables(response.dimensions),
    // compositions
    compositions: mapCompositions(response.compositions),
    // integrations
    allowedIntegrations: mapAllowedIntegrations(response.integrations),
    integrationGroups: mapIntegrationGroups(response.integrations),
    allIntegrations: mapAllIntegrations(response.integrations),
    connectableIntegrations: mapConnectableIntegrations(response.integrations),
    // settings
    attributes: mapAttributes(response.settings),
    // rbac
    rbac: mapRbac(response.rbac),
    // feature_flags
    featureFlags: mapFeatureFlags(response.feature_flags),
    // nav sections (BE-ACC-03/-05)
    reports: mapReports(response.reports),
    navSchedules: mapNavSchedules(response.schedules),
    integrationsConnected: mapIntegrationsConnected(response.integrations_connected),
    dashboardAccess: mapDashboardAccess(response.dashboard_access),
    // meta
    meta: response.meta,
    errors: response.errors || [],
    sectionStatus: mapSectionStatus(response),
    degradedSections: mapDegradedSections(response),
  }
}
